import _debug from 'debug'

import { RetrieveAllBlobIdsResponse } from './lanStorage/blobs'
import { VideoCache } from './VideoCache'
import { INHERITED_UPLOAD_ACCESS_DATE, VideoCacheRecord } from './VideoCacheRecord'
import { isSlttAppStorageEnabled, getAuthorizedStorageProjects, retrieveAllBlobIds, storeBlob, disconnectFromLANStorage, updateBlobUploadedStatus } from './SlttAppStorage'
import { indexBy } from 'underscore'

const log = _debug('sltt:VideoCache.lan')

/**
 *  1. update existing client vcr uploaded status
 *  2. store any blobs that are in the browser cache but not yet in local team storage
 *  3. cache blobs from local team storage that have not been uploaded
 * @param projectsWithUploadAccess
 * @param vcrs
 * @param getVcrsSafeToDelete
 * @returns
 */
export const syncLocalTeamStorageBlobs = async (projectsWithUploadAccess: string[], vcrs: VideoCacheRecord[]) => {
    if (!isSlttAppStorageEnabled()) return
    const authorizedStorageProjects = new Set(await getAuthorizedStorageProjects())
    const browserCachedBlobIds = await VideoCache.getAllVideoBlobIds()
    const response = await retrieveAllBlobIds('syncLocalTeamStorageBlobs')
    // filter response to only include blob ids that start with projectsWithUploadAccess
    const blobDataInProjectsWithUploadAccess = response.filter(b => projectsWithUploadAccess.some(p => b.blobId.startsWith(`${p}/`)))
    await syncVcrUploadedStatus(vcrs, blobDataInProjectsWithUploadAccess)
    await syncBlobsToLocalTeamStorage(vcrs, response, browserCachedBlobIds, authorizedStorageProjects)
    await inheritUploads(browserCachedBlobIds, blobDataInProjectsWithUploadAccess, authorizedStorageProjects)
}

/** Sync intersecting vcrs and blob data
 * If the cache says something has been uploaded then treat that as source of truth and
 * tell the local team storage to reflect that new uploaded status, otherwise
 * update the blob uploaded status in the vcr, if needed
 */
export const syncVcrUploadedStatus = async (vcrs: VideoCacheRecord[], blobDataInProjectsWithUploadAccess: RetrieveAllBlobIdsResponse) => {
    const vcrsByBaseUrl = indexBy(vcrs, vcr => VideoCacheRecord.baseUrl(vcr._id))
    log('1. update existing client vcr uploaded status...')
    const needsSave: { [vcrId: string]: VideoCacheRecord } = {}  
    for (const lanBlobData of blobDataInProjectsWithUploadAccess) {
        // NOTE: lanBlobData.vcrTotalBlobs could be -1 if unknown for uploads,
        // so don't use it to build vcr._id
        const vcrBaseUrl = `${VideoCacheRecord.baseUrl(lanBlobData.blobId)}`
        const vcr = vcrsByBaseUrl[vcrBaseUrl]
        if (!vcr) {
            // this blob is not in our cache, so we don't care about it
            continue
        }
        vcr.setSharedLanBlob(lanBlobData.blobId, true)
        const seqNum = vcr.seqNum(lanBlobData.blobId)
        if (lanBlobData.isUploaded === false) {
            if (vcr.uploadeds.length === 0 && vcr.downloadeds.length === 0) {
                // if vcr.uploadeds.length is false its blobs may have been removed from the
                // cache at some point via vcr.deleteBlobs().
                // If lanBlobData.isUploaded is true then wait until inheritUploads()
                // to handle these
                continue
            }
            if (vcr.uploadeds.length === 0 && vcr.downloadeds.length > 0) {
                // for some reason this vcr was treated as a download (from lan/local team storage)
                // but it did not apply the pending upload status to the blobs
                vcr.uploadeds = new Array(vcr.totalBlobs).fill(true)
                vcr.uploadeds[seqNum - 1] = false
                needsSave[vcr._id] = vcr
                continue
            }
        } else if (vcr.uploadeds.length === 0) {
            continue
        }
        const origVcrUploadedStatus = vcr.uploadeds[seqNum - 1]
        if (origVcrUploadedStatus !== lanBlobData.isUploaded) {
            if (origVcrUploadedStatus === true) {
                // then let the local team storage know that the upload status has changed
                await updateBlobUploadedStatus({
                    blobId: lanBlobData.blobId,
                    isUploaded: true,
                    vcrTotalBlobs: vcr.totalBlobs,
                }, 'syncVcrUploadedStatus')
            } else {
                // origVcrUploadedStatus is false, so lanBlobData.isUploaded must be true
                vcr.uploadeds[seqNum - 1] = true
                needsSave[vcr._id] = vcr
            }
        }
    }
    for (const vcr of Object.values(needsSave)) {
        await vcr.saveToDB()
    }
}

/**
 * store any blobs that are in the browser cache but not yet in local team storage...
 * @param vcrs 
 * @param response 
 * @param browserCachedBlobIds 
 * @param authorizedStorageProjects 
 */
export async function syncBlobsToLocalTeamStorage(vcrs: VideoCacheRecord[], response: RetrieveAllBlobIdsResponse, browserCachedBlobIds: string[], authorizedStorageProjects: Set<string>) {
    const vcrsByBaseUrl = indexBy(vcrs, vcr => VideoCacheRecord.baseUrl(vcr._id))
    const allStoredBlobIds = response.map(b => b.blobId)
    const allStoredBlobIdsSet = new Set(allStoredBlobIds)
    const missingBlobIds = browserCachedBlobIds.filter(blobId => !allStoredBlobIdsSet.has(blobId) && authorizedStorageProjects.has(blobId.split('/')[0]))
    log('2. store any blobs that are in the browser cache but not yet in local team storage...', { missingBlobIds, allStoredBlobIds, browserCachedBlobIds })
    // now save missing blobs to sltt app storage
    // this could take some time depending on how long its been disconnected from the storage
    try {
        const vbs = await VideoCache.getRecordsForKeys(missingBlobIds)
        for (const vb of vbs) {
            const vcr = vcrsByBaseUrl[VideoCacheRecord.baseUrl(vb._id)]
            if (!vcr) throw new Error(`vcr not found for blob ${vb._id}`) // todo: make vcr? how to calculate totalBlobs?
            const isInS3 = vcr.getIsBlobIdInS3(vb._id)
            const result = await storeBlob({ blobId: vb._id, blob: vb.blob, isUploaded: isInS3, vcrTotalBlobs: vcr.totalBlobs }, 'VideoCache.initialize')
            if (result) {
                vcr.setSharedLanBlob(vb._id, true)
            }
        }
    } catch (error) {
        console.error('error saving blobs to disk', error)
        disconnectFromLANStorage()
    }
}

/**
 * @param vcrs 
 * @param browserCachedBlobIds 
 * @param blobDataInProjectsWithUploadAccess 
 * @param authorizedStorageProjects 
 */
export const inheritUploads = async (browserCachedBlobIds: string[], blobDataInProjectsWithUploadAccess: RetrieveAllBlobIdsResponse, authorizedStorageProjects: Set<string>) => {
    log('cache blobs from local team storage that have not been uploaded...')
    const setOfBrowserCachedBlobIds = new Set(browserCachedBlobIds)
    const blobsNeedingUploadMissingFromClient = blobDataInProjectsWithUploadAccess.filter(
        b => !b.isUploaded && !setOfBrowserCachedBlobIds.has(b.blobId) && authorizedStorageProjects.has(b.blobId.split('/')[0])
    )
    for (const blobData of blobsNeedingUploadMissingFromClient) {
        const vcrBaseUrl = VideoCacheRecord.baseUrl(blobData.blobId)
        const vcr = await VideoCacheRecord.get(`${vcrBaseUrl}-${blobData.vcrTotalBlobs}`)
        if (!vcr.accessDate) {
            vcr.accessDate = INHERITED_UPLOAD_ACCESS_DATE // see VideoCacheRecord.isInheritedUpload
        }
        await vcr.tryLoadBlobFromLan(blobData.blobId)
        await vcr.saveToDB()
    }
    // it's possible that we've added uploads beyond our quota
    // so clean up here. We do so AFTER we've added them so that makeSpaceAvailable
    // can remove any older ones we've now just added. Ideally, we'd have a way
    // to just skip those, but the logic seems complicated or requires passing
    // data to makeSpaceAvailable() or some refactored sub-routine.
    await VideoCache.videoCacheDownloader.makeSpaceAvailable()
}
